import { useContext, useEffect, useRef, useState } from "react";
import API from "../api/api";
import { Aliment } from "../data-parsing/aliments/Aliment";
import { ResponseStatus, StatusContextApi } from "../context/status";
import { User } from "../data-parsing/users/User";
import { SpecificUserDiet } from "../data-parsing/users/UserDiet";
import { Doctor } from "../data-parsing/doctors/Doctor";
import {
  appendDoctorTelephonePrefix,
  extractHourFromDate,
  getKeyByValue,
  separateDoctorTelephonePrefix,
} from "../data-parsing/parseUtils";
import { SpecificUserIdAppointment } from "../data-parsing/userAppointment/UserAppointment";
import { InputTypes } from "../data-parsing/InputTypes";

export function useAdmins(query) {
  const [data, setData] = useState();
  const { addMessage, removeMessage } = useContext(StatusContextApi);

  useEffect(() => {
    if (query !== null) {
      let messageId = addMessage(
        ResponseStatus.PENDING,
        -1,
        "Recuperando datos... Por favor, espere"
      );
      API.instance()
        .findAdmins()
        .then((response) => {
          setData(response.admins);
        })
        .finally(() => {
          removeMessage(messageId);
        });
    }
  }, [query]);

  const createAdmin = (admin) => {
    return API.instance()
      .createAdmin(admin)
      .then((response) => {
        if (data) {
          let admins = [...data];
          admins.push(response?.admin);
          setData(admins);
        }
        addMessage(ResponseStatus.RESOLVED, 3000, "Admin creado con éxito");
      })
      .catch((err) => {
        addMessage(ResponseStatus.ERROR, 5000, err.message);
      });
  };

  const deleteAdmin = (adminId) => {
    return API.instance()
      .deleteAdmin(adminId)
      .then(() => {
        let admins = [...data];
        admins = admins.filter((e) => {
          return e._id !== adminId;
        });
        setData(admins);
      });
  };

  const updateAdmin = (admin) => {
    return API.instance().updateAdmin(admin);
  };

  return { admins: data, createAdmin, deleteAdmin, updateAdmin };
}

export function useAliment() {
  const [data, setData] = useState([]);

  const putAliment = async (aliment) => {
    API.instance()
      .updateAliment(aliment)
      .then(() => setData(aliment));
  };

  return {
    recipe: data,
    putAliment,
  };
}

export function useAliments(query) {
  const [data, setData] = useState();
  const { addMessage, removeMessage } = useContext(StatusContextApi);

  useEffect(() => {
    if (query !== null) {
      let messageId = addMessage(
        ResponseStatus.PENDING,
        -1,
        "Recuperando datos... Por favor, espere"
      );
      API.instance()
        .findAliments()
        .then((response) => {
          setData(response);
        })
        .finally(() => {
          removeMessage(messageId);
        });
    }
  }, [query]);

  const createAliment = (aliment) => {
    aliment.food_name = {
      nameES: aliment.nameES,
      nameEN: aliment.nameEN,
      nameFR: aliment.nameFR,
    };
    let messageId = addMessage(ResponseStatus.PENDING, -1, "Procesando...");
    return API.instance()
      .createAliment(aliment)
      .then((response) => {
        if (data) {
          let aliments = [...data];
          aliments.push(response);
          setData(aliments);
        }
        addMessage(ResponseStatus.RESOLVED, 3000, "Alimento creado con éxito");
        return response;
      })
      .catch((err) => {
        addMessage(ResponseStatus.ERROR, 5000, err.message);
      })
      .finally(() => {
        removeMessage(messageId);
      });
  };

  const deleteAliment = (alimentId) => {
    return API.instance()
      .deleteAliment(alimentId)
      .then(() => {
        let aliments = [...data];
        aliments = aliments.filter((e) => {
          return e._id !== alimentId;
        });
        setData(aliments);
      });
  };

  return { aliments: data, createAliment, deleteAliment };
}

export function useArticle() {
  const [data, setData] = useState([]);

  const updateArticle = async (article) => {
    API.instance()
      .updateArticle(article)
      .then(() => setData(article));
  };

  return {
    article: data,
    updateArticle,
  };
}

export function useArticles(query) {
  const [data, setData] = useState();
  const { addMessage, removeMessage } = useContext(StatusContextApi);

  useEffect(() => {
    if (query !== null) {
      API.instance()
        .findArticles()
        .then((response) => {
          setData(response.filter((e) => e.type === "article"));
        })
        .finally(() => { });
    }
  }, [query]);

  const createArticle = (article) => {
    let messageId = addMessage(ResponseStatus.PENDING, -1, "Procesando...");
    return API.instance()
      .createArticle({ ...article, ...{ category: "article" } })
      .then((response) => {
        if (data) {
          let articles = [...data];
          articles.push(response?.doc);
          setData(articles);
        }
        addMessage(ResponseStatus.RESOLVED, 3000, "Artículo creado con éxito");
        return response?.doc;
      })
      .catch((err) => {
        addMessage(ResponseStatus.ERROR, 5000, err.message);
      })
      .finally(() => {
        removeMessage(messageId);
      });
  };

  const deleteArticle = (articleId) => {
    return API.instance()
      .deleteArticle(articleId)
      .then(() => {
        let articles = [...data];
        articles = articles.filter((e) => {
          return e._id !== articleId;
        });
        setData(articles);
      });
  };

  return { articles: data, createArticle, deleteArticle };
}

export function useMyself(query) {
  const [data, setData] = useState();
  const { addMessage, removeMessage } = useContext(StatusContextApi);

  useEffect(() => {
    if (query !== null) {
      let messageId = addMessage(
        ResponseStatus.PENDING,
        -1,
        "Recuperando datos... Por favor, espere"
      );
      API.instance()
        .findMyself()
        .then((response) => {
          setData(response?.admins[0]);
        })
        .finally(() => {
          removeMessage(messageId);
        });
    }
  }, [query]);

  const updateMyself = (newData) => {
    return API.instance()
      .updateMyself(newData)
      .then(() => {
        setData(newData);
        addMessage(
          ResponseStatus.RESOLVED,
          3000,
          "Datos actualizados con éxito"
        );
      });
  };

  const updatePassword = (newData) => {
    return API.instance()
      .updatePassword(newData)
      .then(() => {
        addMessage(
          ResponseStatus.RESOLVED,
          3000,
          "Contraseña modificada con éxito"
        );
      });
  };

  return { myself: data, updateMyself, updatePassword };
}

export function useMedia(folder, id) {
  const [data, setData] = useState();
  const [mediaFolder, setMediaFolder] = useState("");

  useEffect(() => {
    /*if(id) {*/
    API.instance()
      .findMedia(folder, id)
      .then((response) => {
        setData(response);
      })
      .catch((err) => {
        setData({});
      })
      .finally(() => { });
    /*}*/
    setMediaFolder(folder);
  }, [folder, id]);

  const createMedia = (
    id,
    media,
    filename,
    isThumbnail = false,
    type = "Image"
  ) => {
    return API.instance().createMedia(
      mediaFolder,
      id,
      media,
      filename,
      isThumbnail,
      type
    );
  };
  const deleteMedia = (id, filename) => {
    return API.instance().deleteMedia(mediaFolder, id, filename);
  };

  return { media: data, status, createMedia, deleteMedia };
}

export function useMediaProduct(folder, id) {
  const [data, setData] = useState();
  const [mediaFolder, setMediaFolder] = useState("");

  useEffect(() => {
    API.instance()
      .findMedia(folder, id)
      .then((response) => {
        setData(response);
      })
      .catch((err) => {
        setData({});
      })
      .finally(() => { });
    /*}*/
    setMediaFolder(folder);
  }, [folder, id]);

  const createMediaProduct = (
    id,
    media,
    filename,
    isThumbnail = false,
    type = "Image"
  ) => {
    return API.instance().createMediaProduct(
      mediaFolder,
      id,
      media,
      filename,
      isThumbnail,
      type
    );
  };
  const deleteMediaProduct = (id, filename) => {
    return API.instance().deleteMedia(mediaFolder, id, filename);
  };

  return { media: data, status, createMediaProduct, deleteMediaProduct };
}



/**
 * NO FUNCIONA
*/
export function useMediaPdfFile(folder, id) {
  const [data, setData] = useState();
  const [mediaFolder, setMediaFolder] = useState("");

  useEffect(() => {
    API.instance()
      .findMedia(folder, id)
      .then((response) => {
        setData(response);
      })
      .catch((err) => {
        setData({});
      })
      .finally(() => { });
    /*}*/
    setMediaFolder(folder);
  }, [folder, id]);

  const createMediaPdfFile = (
    id,
    media,
    filename,
    isThumbnail = false,
    type = "Image",
    visibleForUser = false
  ) => {
    console.log("ENTRO HOOKS");
    return API.instance().createMediaPdfFile(
      mediaFolder,
      id,
      media,
      filename,
      isThumbnail,
      type,
      visibleForUser
    );
  };
  const deleteMediaPdfFile = (id, filename) => {
    return API.instance().deleteMediaPdfFile(mediaFolder, id, filename);
  };

  return { media: data, status, createMediaPdfFile, deleteMediaPdfFile };
}

export function useVideoCall(message) {
  if (message) {
    API.instance()
      .sendMessage(message)
      .then((response) => {
        console.log("sendMessage response", response);
      });
  }
}

export function useNewUsers(query) {
  const [data, setData] = useState();

  useEffect(() => {
    if (query) {
      API.instance()
        .findNewUsers(query)
        .then((response) => {
          setData(response);
        })
        .finally(() => { });
    }
  }, [query]);

  return { newUsers: data };
}

export function useDoctorAppointments(id) {
  const [data, setData] = useState();
  const { addMessage, removeMessage } = useContext(StatusContextApi);

  const [isLoading, setIsLoading] = useState(true);

  const refresh = () => {
    setIsLoading(true)
  }


  useEffect(() => {
    if (id !== null && isLoading) {
      let messageId = addMessage(
        ResponseStatus.PENDING,
        -1,
        "Recuperando datos... Por favor, espere"
      );
      API.instance()
        .findDoctorAppointments(id)
        .then((response) => {
          let appointments = response[0]?.horas_reservadas;
          appointments?.forEach((appointment) => {
            let date = new Date(appointment.horas_reservadas * 1000);
            appointment.hora_cita = extractHourFromDate(date);
          });
          setData(appointments);
        })
        .finally(() => {
          setIsLoading(false)
          removeMessage(messageId);
        });
    }
  }, [id,refresh]);

  return { appointments: data, refresh };
}

export function useDoctors(query) {
  const [validated, setValidated] = useState();
  const [notValidated, setNotValidated] = useState();
  const { addMessage, removeMessage } = useContext(StatusContextApi);

  useEffect(() => {
    if (query !== null) {
      API.instance()
        .findDoctors()
        .then((response) => {
          let doctors = [...response];
          doctors?.forEach((doctor) => {
            appendDoctorTelephonePrefix(doctor);
          });
          setValidated(doctors);
        })
        .finally(() => { });
      API.instance()
        .findValidates()
        .then((response) => {
          let doctors = [...response];
          doctors?.forEach((doctor) => {
            appendDoctorTelephonePrefix(doctor);
          });
          setNotValidated(doctors);
        })
        .finally(() => { });
    }
  }, [query]);

  const createDoctor = (doctor) => {
    doctor = separateDoctorTelephonePrefix(doctor);
    return API.instance()
      .createDoctor(doctor)
      .then((response) => {
        if (notValidated) {
          let doctors = [...notValidated];
          doctors.push(response?.doc);
          setNotValidated(doctors);
        }
        addMessage(
          ResponseStatus.RESOLVED,
          3000,
          "Nutricionista creado con éxito"
        );
      })
      .catch((err) => {
        addMessage(ResponseStatus.ERROR, 5000, err.message);
      });
  };

  const deleteDoctor = (doctorId) => {
    return API.instance()
      .deleteDoctor(doctorId)
      .then(() => {
        let doctors = [...validated];
        doctors = doctors.filter((e) => {
          return e._id !== doctorId;
        });
        setValidated(doctors);
      });
  };
  const deleteValidate = (doctorId) => {
    return API.instance()
      .deleteDoctor(doctorId)
      .then(() => {
        let doctors = [...notValidated];
        doctors = doctors.filter((e) => {
          return e._id !== doctorId;
        });
        setNotValidated(doctors);
      });
  };

  const updateDoctor = (doctor) => {
    doctor = separateDoctorTelephonePrefix(doctor);
    return API.instance().updateDoctor(doctor);
  };

  return {
    doctors: validated,
    notValidated: notValidated,
    createDoctor,
    deleteDoctor,
    updateDoctor,
    deleteValidate,
  };
}

export function useValidate(id) {
  const [data, setData] = useState([]);

  useEffect(() => {
    if (id !== null) {
      API.instance()
        .findValidate(id)
        .then((response) => {
          let doctor = appendDoctorTelephonePrefix(response);
          setData(doctor);
        })
        .finally(() => { });
    }
  }, [id]);

  const confirmDoctor = (doctor) => {
    doctor = separateDoctorTelephonePrefix(doctor);
    return API.instance().confirmDoctor(doctor);
  };

  return {
    validate: data,
    confirmDoctor,
  };
}

export function useForms(query) {
  const [data, setData] = useState();
  const { addMessage, removeMessage } = useContext(StatusContextApi);

  useEffect(() => {
    if (query !== null) {
      API.instance()
        .findForms(query)
        .then((response) => {
          console.log("forms", response);
          setData(response);
        })
        .finally(() => { });
    }
  }, [query]);

  const createForm = async (form) => {
    let messageId = addMessage(ResponseStatus.PENDING, -1, "Procesando...");

    return API.instance()
      .createForm(form)
      .then((response) => {
        if (data) {
          let forms = [...data];
          forms.push(response?.doc);  // Asumiendo que el backend devuelve el .doc como en artículos
          setData(forms);
        }
        addMessage(ResponseStatus.RESOLVED, 3000, "Informe creado con éxito");
        return response?.doc;  // Devuelve el documento para ser usado más adelante
      })
      .catch((err) => {
        addMessage(ResponseStatus.ERROR, 5000, err.message);
      })
      .finally(() => {
        removeMessage(messageId);
      });
  };

  const updateForm = async (form) => {
    form.history_id = form._id;
    console.log("form", form);
    let messageId = addMessage(ResponseStatus.PENDING, -1, "Procesando...");
    API.instance()
      .updateForm(form)
      .then((response) => {
        addMessage(ResponseStatus.RESOLVED, 3000, "Informe editado con éxito");
        return response;
      })
      .catch((err) => {
        addMessage(ResponseStatus.ERROR, 5000, err.message);
      })
      .finally(() => {
        removeMessage(messageId);
      });
  };

  const deleteForm = async (id) => {
    const messageId = addMessage(ResponseStatus.PENDING, -1, "Procesando...");

    try {
      await API.instance().deleteForm(id);

      // Update local state
      setData((prevData) => prevData.filter((e) => e._id !== id));

      addMessage(ResponseStatus.RESOLVED, 3000, "Informe borrado con éxito");
    } catch (err) {
      addMessage(ResponseStatus.ERROR, 5000, err.message);
    } finally {
      removeMessage(messageId);
    }
  };


  return { forms: data, createForm, updateForm, deleteForm };
}

export function useLegend(query) {
  const [data, setData] = useState([]);

  useEffect(() => {
    if (query) {
      console.log("LEGEND", API.instance().getLegend());
      API.instance().getLegend().then(setData);
    }
  }, [query]);

  return data;
}

export function useLogs(query) {
  const [data, setData] = useState([]);

  useEffect(() => {
    if (query != null) {
      API.instance().findLogs().then(setData);
    }
  }, [query]);

  return data;
}

export function useRecipes(query) {
  const [data, setData] = useState();
  const { addMessage, removeMessage } = useContext(StatusContextApi);
  const [testedRecipe, setTestedRecipe] = useState();

  useEffect(() => {
    if (query !== null) {
      let messageId = addMessage(
        ResponseStatus.PENDING,
        -1,
        "Recuperando datos... Por favor, espere"
      );
      API.instance()
        .findRecipes()
        .then((response) => {
          setData(response);
        })
        .finally(() => {
          removeMessage(messageId);
        });
    }
  }, [query]);

  const createRecipe = async (recipe) => {
    API.instance()
      .createRecipe(recipe)
      .then((response) => {
        let recipes = [...data];
        recipes.push(response);
        setData(recipes);
        addMessage(ResponseStatus.RESOLVED, 3000, "Receta creada con éxito");
      })
      .catch((err) => {
        addMessage(ResponseStatus.ERROR, 5000, err.message);
      });
  };

  const testRecipe = async (recipe) => {
    try {
      const response = await API.instance().testRecipe(recipe);
      setTestedRecipe(response);
      addMessage(
        ResponseStatus.RESOLVED,
        3000,
        "Compruebe el nombre y preparación de la receta antes de guardarla"
      );
      return response;
    } catch (err) {
      addMessage(ResponseStatus.ERROR, 5000, err.message);
      throw err;
    }
  };

  return { recipes: data, createRecipe, testRecipe, testedRecipe };
}

export function useRecipe() {
  const [data, setData] = useState([]);

  const putRecipe = async (recipe) => {
    API.instance().updateRecipe(recipe).then(setData);
  };

  return {
    recipe: data,
    putRecipe,
  };
}

export function useSpreadsheet() {
  const [data, setData] = useState();
  const { addMessage } = useContext(StatusContextApi);

  const postSpreadsheet = async (file, sheetNames) => {
    API.instance()
      .uploadSpreadsheet(file, sheetNames)
      .then((response) => {
        setData(response);
        addMessage(ResponseStatus.RESOLVED, 3000, "Datos creados con éxito");
      })
      .catch((err) => {
        addMessage(ResponseStatus.ERROR, 5000, err.message);
      });
  };

  return {
    spreadsheet: data,
    postSpreadsheet,
  };
}

export function useStats(query) {
  const [week, setWeek] = useState([]);
  const [twoWeeks, setTwoWeeks] = useState([]);
  const [month, setMonth] = useState([]);
  const [sixMonths, setSixMonths] = useState([]);

  useEffect(() => {
    if (query !== null) {
      API.instance()
        .findStats("week")
        .then((response) => {
          setWeek(response);
        });
      API.instance()
        .findStats("twoWeeks")
        .then((response) => {
          setTwoWeeks(response);
        });
      API.instance()
        .findStats("month")
        .then((response) => {
          setMonth(response);
        });
      API.instance()
        .findStats("sixMonths")
        .then((response) => {
          setSixMonths(response);
        });
    }
  }, [query]);

  return {
    stats: {
      week: week,
      twoWeeks: twoWeeks,
      month: month,
      sixMonths: sixMonths,
    },
  };
}

export function useUser(id) {
  const [data, setData] = useState();
  console.log("id", id);
  useEffect(() => {
    if (id) {
      API.instance()
        .findUser(id)
        .then((response) => {
          /*           console.log("userResponse", response[0]);
           */ setData(response[0]);
        })
        .finally(() => { });
    }
  }, [id]);

  return { user: data };
}

export function useUserDiets(id) {
  const [data, setData] = useState();

  useEffect(() => {
    if (id) {
      API.instance()
        .findUserDiets(id)
        .then((response) => {
          /*           console.log("diets", response);
           */ setData(response);
        })
        .finally(() => { });
    }
  }, [id]);

  return { diets: data };
}

export function useUsers(query) {
  const [data, setData] = useState();

  useEffect(() => {
    if (query !== null) {
      API.instance()
        .findUsers()
        .then((response) => {
          setData(response.user);
        })
        .finally(() => { });
    }
  }, [query]);

  const createUser = (user) => {
    return API.instance()
      .createUser(user)
      .then((response) => {
        if (status === ResponseStatus.RESOLVED) {
          let users = [...data];
          users.push(response.doc);
          setData(users);
        }
      });
  };

  const deleteUser = (userId) => {
    return API.instance()
      .deleteUser(userId)
      .then(() => {
        let users = [...data];
        users = users.filter((e) => {
          return e._id !== userId;
        });
        setData(users);
      });
  };

  const updateUser = (user) => {
    return API.instance().updateUser(user);
  };

  return { users: data, createUser, deleteUser, updateUser };
}

export function useUserAppointments(id) {
  const [data, setData] = useState();
  const { addMessage, removeMessage } = useContext(StatusContextApi);

  useEffect(() => {
    if (id !== null) {
      API.instance()
        .findUserAppointments(id)
        .then((response) => {
          let appointments = response[0]?.horas_reservadas;
          appointments?.forEach((appointment) => {
            let date = new Date(appointment.horas_reservadas * 1000);
            appointment.hora_cita = extractHourFromDate(date);
          });
          /*           console.log("appointments", appointments);
           */ setData(appointments);
        })
        .finally(() => { });
    }
  }, [id]);

  const createAppointment = async (appointment) => {
    let date = new Date(appointment.horas_reservadas * 1000);
    date.setHours(parseInt(appointment.hora_cita.split(":")[0]));
    date.setMinutes(parseInt(appointment.hora_cita.split(":")[1]));
    appointment.horas_reservadas = date.getTime() / 1000;
    let messageId = addMessage(ResponseStatus.PENDING, -1, "Procesando...");
    API.instance()
      .createUserAppointment(appointment)
      .then((response) => {
        if (data) {
          let appointments = [...data];
          appointments.push(response);
          setData((prev)=> [...prev, ...appointments]);
        }
        addMessage(ResponseStatus.RESOLVED, 3000, "Sesión creada con éxito");
        return response;
      })
      .catch((err) => {
        addMessage(ResponseStatus.ERROR, 5000, err.message);
      })
      .finally(() => {
        removeMessage(messageId);
      });
  };

  const deleteAppointment = async (id) => {
    console.log("delete appointment with id: ", id);
    API.instance()
      .deleteUserAppointment(id)
      .then((response) => {
        console.log("response delete appointment", response);
        let appointments = [...data];
        appointments = appointments.filter((e) => {
          return e._id !== id;
        });
        setData(appointments);
      });
  };

  return { userAppointments: data, createAppointment, deleteAppointment };
}

export function useUsedHours(query) {
  const [data, setData] = useState();

  useEffect(() => {
    if (query) {
      let invalidValues = Object.values(query).filter((value) => {
        return !value;
      });
      if (invalidValues.length > 0) {
        return;
      }
      //query.timestamp = new Date().getTime() / 1000;
      API.instance()
        .findUsedHours(query)
        .then((response) => {
          setData(
            response[0].horas_reservadas?.map((hour) => {
              let date = new Date(hour.horas_reservadas * 1000);
              return extractHourFromDate(date);
            })
          );
        })
        .finally(() => { });
    }
  }, [JSON.stringify(query)]);

  return { usedHours: data };
}

export function useVideo() {
  const [data, setData] = useState([]);

  const updateVideo = async (video) => {
    API.instance()
      .updateArticle(video)
      .then(() => setData(video));
  };

  return {
    video: data,
    updateVideo,
  };
}

export function useVideos(query) {
  const [data, setData] = useState();
  const { addMessage, removeMessage } = useContext(StatusContextApi);

  useEffect(() => {
    if (query !== null) {
      API.instance()
        .findArticles()
        .then((response) => {
          setData(response.filter((e) => e.type === "video"));
        });
    }
  }, [query]);

  const createVideo = (video) => {
    let messageId = addMessage(ResponseStatus.PENDING, -1, "Procesando...");
    return API.instance()
      .createArticle({ ...video, ...{ category: "video" } })
      .then((response) => {
        if (data) {
          let videos = [...data];
          videos.push(response?.doc);
          setData(videos);
        }
        addMessage(ResponseStatus.RESOLVED, 3000, "Vídeo creado con éxito");
        return response?.doc;
      })
      .catch((err) => {
        addMessage(ResponseStatus.ERROR, 5000, err.message);
      })
      .finally(() => {
        removeMessage(messageId);
      });
  };

  const deleteVideo = (videoId) => {
    return API.instance()
      .deleteArticle(videoId)
      .then(() => {
        let videos = [...data];
        videos = videos.filter((e) => {
          return e._id !== videoId;
        });
        setData(videos);
      });
  };

  return { videos: data, createVideo, deleteVideo };
}

export function usePodcast() {
  const [data, setData] = useState([]);

  const updatePodcast = async (podcast) => {
    API.instance()
      .updateArticle(podcast)
      .then(() => setData(podcast));
  };

  return {
    podcast: data,
    updatePodcast,
  };
}

export function usePodcasts(query) {
  const [data, setData] = useState();
  const { addMessage, removeMessage } = useContext(StatusContextApi);

  useEffect(() => {
    if (query !== null) {
      API.instance()
        .findArticles()
        .then((response) => {
          console.log(response);
          setData(response.filter((e) => e.type === "podcast"));
        });
    }
  }, [query]);

  const createPodcast = (podcast) => {
    let messageId = addMessage(ResponseStatus.PENDING, -1, "Procesando...");
    return API.instance()
      .createArticle({ ...podcast, ...{ category: "podcast" } })
      .then((response) => {
        if (data) {
          let podcasts = [...data];
          podcasts.push(response?.doc);
          setData(podcasts);
        }
        addMessage(ResponseStatus.RESOLVED, 3000, "Pódcast creado con éxito");
        return response?.doc;
      })
      .catch((err) => {
        addMessage(ResponseStatus.ERROR, 5000, err.message);
      })
      .finally(() => {
        removeMessage(messageId);
      });
  };

  const deletePodcast = (podcastId) => {
    return API.instance()
      .deleteArticle(podcastId)
      .then(() => {
        let podcasts = [...data];
        podcasts = podcasts.filter((e) => {
          return e._id !== podcasts;
        });
        setData(podcasts);
      });
  };

  return { podcasts: data, createPodcast, deletePodcast };
}

export function useProduct() {
  const [data, setData] = useState([]);

  const updateProduct = async (product) => {
    API.instance()
      .updateProduct(product)
      .then(() => setData(product));
  };

  return {
    product: data,
    updateProduct,
  };
}

export function useProducts(query) {
  const [data, setData] = useState();
  const { addMessage, removeMessage } = useContext(StatusContextApi);

  useEffect(() => {
    if (query !== null) {
      API.instance()
        .findProducts()
        .then((response) => {
          console.log("PRODUCTOS");
          console.log(response);
          setData(response);
        });
    }
  }, [query]);

  const createProduct = (product) => {
    let messageId = addMessage(ResponseStatus.PENDING, -1, "Procesando...");

    return API.instance()
      .createProduct({ ...product })
      .then((response) => {
        if (data) {
          let products = [...data];
          products.push(response?.doc);
          setData(products);
        }
        addMessage(ResponseStatus.RESOLVED, 3000, "Producto creado con éxito");
        return response?.doc;
      })
      .catch((err) => {
        addMessage(ResponseStatus.ERROR, 5000, err.message);
      })
      .finally(() => {
        removeMessage(messageId);
      });
  };

  const deleteProduct = (productId) => {
    return API.instance()
      .deleteProduct(productId)
      .then(() => {
        let products = [...data];
        products = products.filter((e) => {
          return e._id !== productId;
        });
        setData(products);
      });
  };

  return { products: data, createProduct, deleteProduct };
}

export function useOrder() {
  const [data, setData] = useState([]);

  const updateOrder = async (order) => {
    API.instance()
      .updateOrder(order)
      .then(() => setData(order));
  };

  return {
    order: data,
    updateOrder,
  };
}

export function useOrders(query) {
  const [data, setData] = useState();
  const { addMessage, removeMessage } = useContext(StatusContextApi);

  useEffect(() => {
    if (query !== null) {
      API.instance()
        .findOrders()
        .then((response) => {
          console.log(response);
          setData(response);
        });
    }
  }, [query]);

  const deleteOrder = (orderId) => {
    /* return API.instance()
      .deleteArticle(podcastId)
      .then(() => {
        let podcasts = [...data];
        podcasts = podcasts.filter((e) => {
          return e._id !== podcasts;
        });
        setData(podcasts);
      }); */
  };

  return { orders: data, deleteOrder };
}

// Auxiliary hooks, independent from endpoints

const cache = {};
let status = {};

export const useCollection = (collection) => {
  const [data, setData] = useState([]);

  useEffect(() => {
    const getData = async () => {
      if (!collection) return;
      if (!status[collection.toString]) {
        status[collection.toString] = "idle";
      }
      if (
        cache[collection.toString] ||
        status[collection.toString] === "fetching"
      ) {
        if (cache[collection.toString]?.user) {
          return cache[collection.toString].user;
        } else if (cache[collection.toString]?.horas_reservadas) {
          return cache[collection.toString].horas_reservadas;
        } else {
          return cache[collection.toString];
        }
        //setData();
      } else {
        status[collection.toString] = "fetching";
        switch (collection.toString) {
          case Aliment.toString: {
            const response = API.instance().findAliments();
            cache[Aliment.toString] = response; // set response in cache;
            return response;
          }
          case Doctor.toString: {
            const response = API.instance().findDoctors();
            cache[Doctor.toString] = response; // set response in cache;
            return response;
          }
          case SpecificUserDiet(collection?.user_id).toString: {
            if (!collection.user_id) return [];
            const response = API.instance().findUserDiets(collection?.user_id);
            cache[SpecificUserDiet(collection?.user_id).toString] = response; // set response in cache;
            return response;
          }
          case User.toString: {
            const response = API.instance().findUsers();
            cache[User.toString] = response; // set response in cache;
            return response;
          }
          case SpecificUserIdAppointment(collection?.user_id).toString: {
            if (!collection.user_id) return [];
            const response = API.instance().findUserAppointments(
              collection?.user_id
            );
            cache[SpecificUserIdAppointment(collection?.user_id).toString] =
              response; // set response in cache;
            return response;
          }
          default:
            break;
        }
      }
    };

    getData().then((response) => {
      /*status = 'fetched';*/
      if (response?.user) {
        setData(response?.user);
      } else if (response && response[0]?.horas_reservadas) {
        setData(response[0]?.horas_reservadas);
      } else {
        setData(response);
      }
    });
  }, [collection?.toString]);

  return data;
};

export const usePrevious = (value) => {
  const ref = useRef();
  useEffect(() => {
    if (value) {
      ref.current = [...value];
    }
  });
  return ref.current;
};

export function useIsVisible(ref) {
  const [isIntersecting, setIntersecting] = useState(false);

  useEffect(() => {
    if (ref?.current) {
      const observer = new IntersectionObserver(([entry]) =>
        setIntersecting(entry.isIntersecting)
      );

      observer.observe(ref?.current);
      return () => {
        observer.disconnect();
      };
    }
  }, [ref]);

  return isIntersecting;
}
